import { useCallback, type FormEvent } from 'react'
import { useLoginMutation } from '@/store/api/authApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  selectIsSubmitting,
  selectLoginError,
  selectLoginFields,
} from '@/store/selectors/authSelectors'
import {
  loginFailed,
  loginFieldChanged,
  loginSubmitted,
  loginSucceeded,
} from '@/store/slices/authSlice'
import type { LoginField } from '@/types/domain'
import { getAuthErrorMessage } from '@/utils/getAuthErrorMessage'
import { validateLoginCredentials } from '@/utils/validateLoginCredentials'
import { useAppRoute } from '@/hooks/useAppRoute'

export function useLoginForm() {
  const dispatch = useAppDispatch()
  const fields = useAppSelector(selectLoginFields)
  const loginError = useAppSelector(selectLoginError)
  const isSubmitting = useAppSelector(selectIsSubmitting)
  const [loginRequest] = useLoginMutation()
  const { navigateToShell } = useAppRoute()

  const onEmailChange = useCallback(
    (value: string) => {
      dispatch(loginFieldChanged({ field: 'email', value }))
    },
    [dispatch],
  )

  const onPasswordChange = useCallback(
    (value: string) => {
      dispatch(loginFieldChanged({ field: 'password', value }))
    },
    [dispatch],
  )

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      const validation = validateLoginCredentials(fields)
      if (!validation.ok) {
        dispatch(loginFailed(validation.message))
        return
      }

      dispatch(loginSubmitted())
      const result = await loginRequest({
        email: fields.email.trim(),
        password: fields.password,
      })

      if (result.error) {
        dispatch(loginFailed(getAuthErrorMessage(result.error)))
        return
      }

      dispatch(loginSucceeded(result.data))
      navigateToShell()
    },
    [dispatch, fields, loginRequest, navigateToShell],
  )

  return {
    email: fields.email,
    password: fields.password,
    loginError,
    isSubmitting,
    onEmailChange,
    onPasswordChange,
    onSubmit,
    onFieldChange: (field: LoginField, value: string) => {
      if (field === 'email') {
        onEmailChange(value)
      } else {
        onPasswordChange(value)
      }
    },
  }
}
